import { isEmptyArray, isEmptyObject, capitalizeFirstLetter } from '../modules/utilities.js'
import { getNotEmptyString, getString } from '../modules/dataValidator.js'
import { SG1Logs } from '../modules/logs.js'

/**
 * Récupère le contract et ses informations (advise + SSE)
 * à afficher dans la bande second contract de la HP
 * Retourne les données traités sous forme d'objet
 * @param {Array} adviseDatas : Données advise
 * @param {Object} sseDatas : Données back de la bande second contract
 * @return {Object} Contract d'interface complet ou {}
 */
export function secondContractDataFormatter(sseDatas, adviseDatas) {
   let contractDRO = {}
   let contractInfos = {}
   let contractInterface = {}

   try {
      contractInterface = sseDatas
      contractDRO = getContractWithDRO(adviseDatas)

      if (isEmptyObject(contractDRO)) {
         contractDRO.DROText = ''
         contractInfos = getContract(sseDatas) // fallback si pas de reco dro
      } else {
         contractInfos = getContract(sseDatas, contractDRO.CID)
      }

      if (!isEmptyObject(contractInfos)) {
         contractInterface.firstName = getFirstName(adviseDatas, contractInfos.CID)
         contractInterface = { ...contractInterface, ...contractInfos, ...contractDRO }
         delete contractInterface.contracts

         // insertion prénom dans le titre de la bande
         if (contractInterface.firstName !== '') {
            contractInterface.title = sseDatas.titleWithFirstName.replace('{firstName}', contractInterface.firstName)
         }

         contractInterface = validateCI(contractInterface)
      }
   } catch (error) {
      console.error('secondContractDataFormatter :', error)
      SG1Logs.incLog('Err_FMTR')
      SG1Logs.sentryEvent({ error: error, name: 'SecondContract DataFormatter' })
   }
   return contractInterface
}

function validateCI(CI) {
   let validContractInterface = {}
   try {
      getNotEmptyString(CI.title, 'title')
      getNotEmptyString(CI.titleWithFirstName, 'titleWithFirstName')
      getNotEmptyString(CI.CID, 'CID')
      getNotEmptyString(CI.pill, 'pill')
      getNotEmptyString(CI.title, 'title')

      if (typeof CI.CTA === 'object') {
         getNotEmptyString(CI.CTA.label, 'CTA label')
         getNotEmptyString(CI.CTA.URL, 'CTA URL')
         let ga = CI.CTA.analytics
         getNotEmptyString(ga.pdbZid, 'CTA pdbZid', true)
         getString(ga.pdbSc, 'CTA pdbSc', true)
         getString(ga.pdbIdc, 'CTA pdbIdc', true)
         getNotEmptyString(ga.oeventCategory, 'CTA oeventCategory', true)
         getNotEmptyString(ga.oeventAction, 'CTA oeventAction', true)
         getNotEmptyString(ga.oeventLabel, 'CTA oeventLabel', true)
         getNotEmptyString(ga.oeventOther, 'CTA oeventOther', true)
      }

      let contractCard = CI.contractCard
      getNotEmptyString(contractCard.title, 'contract card title')
      getNotEmptyString(contractCard.URL, 'contract card URL')
      getNotEmptyString(contractCard.description, 'contract card description')
      let ga = CI.contractCard.analytics
      getNotEmptyString(ga.pdbZid, 'contract card pdbZid', true)
      getString(ga.pdbSc, 'contract card pdbSc', true)
      getString(ga.pdbIdc, 'contract card pdbIdc', true)
      getNotEmptyString(ga.oeventCategory, 'contract card oeventCategory', true)
      getNotEmptyString(ga.oeventAction, 'contract card oeventAction', true)
      getNotEmptyString(ga.oeventLabel, 'contract card oeventLabel', true)
      getNotEmptyString(ga.oeventOther, 'contract card oeventOther', true)

      let mobileCard = CI.mobileCard
      getNotEmptyString(mobileCard.title, 'mobile card title')
      getNotEmptyString(mobileCard.URL, 'mobile card URL')
      getNotEmptyString(mobileCard.description, 'mobile card description')
      ga = CI.mobileCard.analytics
      getNotEmptyString(ga.pdbZid, 'mobile card pdbZid', true)
      getString(ga.pdbSc, 'mobile card pdbSc', true)
      getString(ga.pdbIdc, 'mobile card pdbIdc', true)
      getNotEmptyString(ga.oeventCategory, 'mobile card oeventCategory', true)
      getNotEmptyString(ga.oeventAction, 'mobile card oeventAction', true)
      getNotEmptyString(ga.oeventLabel, 'mobile card oeventLabel', true)
      getNotEmptyString(ga.oeventOther, 'mobile card oeventOther', true)

      validContractInterface = CI
   } catch (error) {
      console.error('validateCI', error)
      SG1Logs.incLog('Err_FMTR')
      SG1Logs.sentryEvent({ error: error, name: 'SecondContract DataFormatter' })
   }
   return validContractInterface
}

/**
 * Récupere le CID et le texte DRO du contract remonté par Advise
 * @param {Object} adviseDatas : Données advise
 * @return {Object} objet avec CID et texte DRO : {CID: '1234', DROText: 'mon text dro'}
 */
function getContractWithDRO(adviseDatas) {
   let contexts = [
      'acc-digital_occas_marchand_second_contratdro',
      'acc-digital_regulier_marchand_second_contratdro',
      'acc-digital_occas-mob_marchand_second_contratdro',
      'acc-digital_regulier-mob_marchand_second_contratdro',
   ]
   let contractDatas = {}
   let returnContractDatas = {}
   try {
      contexts.forEach((context) => {
         if (adviseDatas.hasOwnProperty(context)) {
            contractDatas = adviseDatas[context]
         }
      })

      if (typeof contractDatas[0] !== 'undefined' && contractDatas[0].text && contractDatas[0].text !== '' && contractDatas[0].cid && contractDatas[0].cid !== '') {
         returnContractDatas.CID = contractDatas[0].cid
         returnContractDatas.DROText = contractDatas[0].text
      }
   } catch (error) {
      console.error('getContractWithDRO', error)
      SG1Logs.incLog('Err_FMTR')
      SG1Logs.sentryEvent({ error: error, name: 'SecondContract DataFormatter' })
   }
   return returnContractDatas
}

/**
 * Récupere un second contract depuis la liste de contract retournée par le SSE
 * Si CID passé : retourne le contract avec le CID correspondant || objet vide si pas de match
 * Si CID non passé : retourne le premier contract de la liste
 * @param {Array} sseDatas : Données SSE
 * @param {String} CID : CID du contract à extraire
 * @return {Object} Le second contract selectionné
 */
function getContract(sseDatas, CID) {
   let returnedContract = {}
   try {
      returnedContract = sseDatas.contracts[0]
      for (const contract of sseDatas.contracts) {
         if (CID === contract.CID) {
            returnedContract = contract
            return returnedContract
         }
      }
   } catch (error) {
      console.error('getContract', error)
      SG1Logs.incLog('Err_FMTR')
      SG1Logs.sentryEvent({ error: error, name: 'SecondContract DataFormatter' })
   }
   return returnedContract
}

/**
 * Récupere le prénom depuis le context Advise 'marchand_second_contrat'
 * Si CID passé : retourne le prénom avec le CID correspondant || string vide
 * @param {Array} adviseDatas : Données advise
 * @param {String} CID : CID du contract
 * @return {String} Le prénom avec un majuscule ou ''
 */
function getFirstName(adviseDatas, CID) {
   let contexts = [
      'acc-digital_occas_marchand_second_contrat',
      'acc-digital_regulier_marchand_second_contrat',
      'acc-digital_occas-mob_marchand_second_contrat',
      'acc-digital_regulier-mob_marchand_second_contrat',
   ]
   let firstName = ''
   try {
      for (const [index, context] of contexts.entries()) {
         if (adviseDatas.hasOwnProperty(context)) {
            if (typeof CID !== 'undefined') {
               let contract = {}
               contract = adviseDatas[context].filter((item) => item.cid === CID)
               if (!isEmptyArray(contract) && contract[0].text) {
                  firstName = contract[0].text
               }
            }
         }
      }
   } catch (error) {
      console.error('getFirstName', error)
      SG1Logs.incLog('Err_FMTR')
      SG1Logs.sentryEvent({ error: error, name: 'SecondContract DataFormatter' })
   }

   return capitalizeFirstLetter(firstName)
}
